import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Camera } from "lucide-react";

/**
 * GallerySlideshow — auto-advancing community-photo carousel for the landing page.
 * Accepts `images` array of {url, caption?} OR plain URL strings.
 * Auto-advances every 5 seconds, pauses on hover, supports keyboard / arrow navigation.
 * Returns null when no images are configured.
 *
 * Variants:
 *   - full (default): full-width section with heading + counter chrome.
 *   - hero: compact, headless card for placement inside the hero column.
 */
export default function GallerySlideshow({ images = [], variant = "full" }) {
  const items = (images || [])
    .map((i) => (typeof i === "string" ? { url: i } : i))
    .filter((i) => i && i.url);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (items.length <= 1 || paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length, paused]);

  if (items.length === 0) return null;

  const go = (dir) => setIndex((i) => (i + dir + items.length) % items.length);
  const cur = items[index];

  // ─── HERO VARIANT — compact, lives inside another container ──────────
  if (variant === "hero") {
    return (
      <div
        data-testid="gallery-slideshow-hero"
        className="relative hairline border-zinc-700 bg-zinc-950 overflow-hidden group"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="aspect-[4/5] sm:aspect-[3/4] relative">
          {items.map((it, i) => (
            <img
              key={i}
              src={it.url}
              alt={it.caption || `Community photo ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                i === index ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
          {/* Top-left chip */}
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 hairline bg-black/70 border-orange-600/40">
            <Camera size={10} className="text-orange-500" />
            <span className="font-mono text-[9px] tracking-[0.25em] text-orange-500 uppercase">
              FROM THE PACK
            </span>
          </div>
          {/* Counter top-right */}
          <div className="absolute top-3 right-3 font-mono text-[9px] tracking-[0.25em] text-white/80 uppercase bg-black/70 px-2 py-1 hairline border-zinc-700">
            {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </div>

          {/* Caption strip */}
          {cur.caption && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent p-4">
              <p
                className="font-display text-base sm:text-lg text-white tracking-wide leading-tight"
                data-testid="gallery-caption-hero"
              >
                {cur.caption}
              </p>
            </div>
          )}

          {items.length > 1 && (
            <>
              <button
                onClick={() => go(-1)}
                aria-label="Previous photo"
                data-testid="gallery-prev-hero"
                className="absolute left-2 top-1/2 -translate-y-1/2 hairline bg-black/60 border-zinc-700 p-1.5 text-white opacity-0 group-hover:opacity-100 hover:bg-orange-600 hover:border-orange-600 transition"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => go(1)}
                aria-label="Next photo"
                data-testid="gallery-next-hero"
                className="absolute right-2 top-1/2 -translate-y-1/2 hairline bg-black/60 border-zinc-700 p-1.5 text-white opacity-0 group-hover:opacity-100 hover:bg-orange-600 hover:border-orange-600 transition"
              >
                <ChevronRight size={16} />
              </button>

              {/* Dot indicators */}
              <div className="absolute bottom-2 right-3 flex gap-1">
                {items.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to photo ${i + 1}`}
                    data-testid={`gallery-dot-hero-${i}`}
                    className={`h-1 transition-all ${
                      i === index ? "w-5 bg-orange-500" : "w-2 bg-white/40 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── FULL VARIANT (default) ──────────────────────────────────────────
  return (
    <section
      data-testid="gallery-slideshow"
      className="border-t border-zinc-800 bg-black"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">
              <Camera size={11} className="inline mr-1.5 -mt-0.5" />
              FROM THE PACK · LATEST
            </p>
            <h2 className="font-display text-3xl sm:text-5xl text-white mt-1 leading-[0.95]">
              Moments from <span className="text-orange-500">the road.</span>
            </h2>
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
            {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </p>
        </div>

        {/* Stage */}
        <div className="relative hairline border-zinc-800 bg-zinc-950 overflow-hidden group">
          <div className="aspect-[16/9] sm:aspect-[21/9] relative">
            {items.map((it, i) => (
              <img
                key={i}
                src={it.url}
                alt={it.caption || `Community photo ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                  i === index ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
            {/* Bottom gradient + caption */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-5 sm:p-8">
              {cur.caption && (
                <p
                  className="font-display text-xl sm:text-3xl text-white tracking-wide leading-tight max-w-3xl"
                  data-testid="gallery-caption"
                >
                  {cur.caption}
                </p>
              )}
            </div>

            {items.length > 1 && (
              <>
                <button
                  onClick={() => go(-1)}
                  aria-label="Previous photo"
                  data-testid="gallery-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 hairline bg-black/60 border-zinc-700 p-2 text-white opacity-0 group-hover:opacity-100 hover:bg-orange-600 hover:border-orange-600 transition"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => go(1)}
                  aria-label="Next photo"
                  data-testid="gallery-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 hairline bg-black/60 border-zinc-700 p-2 text-white opacity-0 group-hover:opacity-100 hover:bg-orange-600 hover:border-orange-600 transition"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>

          {/* Dot indicators */}
          {items.length > 1 && (
            <div className="absolute bottom-3 right-3 sm:right-6 flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                  data-testid={`gallery-dot-${i}`}
                  className={`h-1.5 transition-all ${
                    i === index ? "w-8 bg-orange-500" : "w-3 bg-zinc-600 hover:bg-zinc-400"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
